import { type ReassignmentDetail as Reassignment, useReassignmentsQuery } from '@cartwheel/api/v1'
import {
  Badge,
  DataTable,
  DropdownFilter,
  type DropdownFilterItem,
  FilterBar,
  Pagination,
  ReassignmentStatusBadge,
} from '@cartwheel/components'
import { useEffect, useState } from 'react'
import { formatReassignmentDateLong } from '../utils/format-reassignment-date'
import { ReassignmentDetailWrapper } from './reassignment-detail-modal'

type SortColumn = 'status' | 'clinician' | 'type' | 'pod' | 'effective_date' | 'created_at'
type SortDirection = 'asc' | 'desc'

type ReassignmentListProps = {
  canReview: boolean
  userId?: string
}

export default function ReassignmentList({ canReview = false, userId }: ReassignmentListProps) {
  // Sorting state (default: status asc)
  const [sortColumn, setSortColumn] = useState<SortColumn>('status')
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc')

  // Filter state
  const [superviseeFilter, setSuperviseeFilter] = useState<string[]>([])
  const [typeFilter, setTypeFilter] = useState<Array<'single_client' | 'entire_caseload'>>([])
  const [teamFilter, setTeamFilter] = useState<string[]>([])
  const [statusFilter, setStatusFilter] = useState<string[]>([])

  // Pagination state
  const [page, setPage] = useState<number>(1)
  const [perPage, setPerPage] = useState<number>(20)

  // Modal state
  const [selectedReassignmentId, setSelectedReassignmentId] = useState<string | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)

  // Fetch data with server-side sorting, filtering, and pagination
  const { data, isLoading, error } = useReassignmentsQuery({
    query: {
      sort_by: sortColumn,
      sort_direction: sortDirection,
      'clinician_ids[]': superviseeFilter.length > 0 ? superviseeFilter : undefined,
      'reassignment_types[]': typeFilter.length > 0 ? typeFilter : undefined,
      'team_ids[]': teamFilter.length > 0 ? teamFilter : undefined,
      'statuses[]': statusFilter.length > 0 ? statusFilter : undefined,
      page: page,
      per_page: perPage,
      user_id: userId,
    },
  })

  // Populate the filter options

  const typeOptions: DropdownFilterItem[] = [
    { value: 'single_client', label: 'Single Client' },
    { value: 'entire_caseload', label: 'Entire Caseload' },
  ]

  const statusOptions: DropdownFilterItem[] = [
    { value: 'initiated', label: canReview ? 'To Review' : 'Pending Review' },
    { value: 'assigned_to_cc', label: 'Assigned to CC' },
    { value: 'approved', label: 'Approved' },
    { value: 'denied', label: 'Denied' },
    { value: 'cancelled', label: 'Cancelled' },
  ]

  // Reset to page 1 when filters or sorting changes
  // biome-ignore lint/correctness/useExhaustiveDependencies: We intentionally want to reset page when these values change
  useEffect(() => {
    setPage(1)
  }, [sortColumn, sortDirection, superviseeFilter, typeFilter, teamFilter, statusFilter])

  // Handle sort clicks
  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortColumn(column)
      setSortDirection('asc')
    }
  }

  // Handle pagination changes
  const handlePageChange = (newPage: number) => {
    setPage(newPage)
  }

  const handleItemsPerPageChange = (newPerPage: number) => {
    setPerPage(newPerPage)
    setPage(1) // Reset to first page when changing items per page
  }
  // Handle row click to open modal
  const handleRowClick = (reassignmentId: string) => {
    setSelectedReassignmentId(reassignmentId)
    setIsModalOpen(true)
  }

  const handleModalClose = () => {
    setIsModalOpen(false)
    setSelectedReassignmentId(null)
  }

  // Format helpers
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  const formatType = (reassignment: Reassignment) => {
    const isSingleClient = !!reassignment.journey_info.client_name
    const isRepeat = isSingleClient
      ? reassignment.journey_info.is_repeat_reassignment
      : reassignment.journey_info.has_any_repeat_reassignments

    return (
      <div>
        <div className="font-semibold flex items-center gap-2">
          {isSingleClient ? 'Single client' : 'Entire caseload'}
          {isRepeat && <Badge label="Repeat" color="gold" />}
        </div>
        <div className="text-charcoal-50">
          {isSingleClient
            ? reassignment.journey_info.client_name
            : `${reassignment.journey_info.count} clients`}
        </div>
      </div>
    )
  }

  if (isLoading) return <div>Loading...</div>
  if (error) return <div>Error loading reassignments</div>

  const items = data?.items || []

  return (
    <>
      {canReview && (
        <FilterBar>
          <DropdownFilter
            label="Supervisee"
            items={data?.filters.clinicians || []}
            defaultValue={superviseeFilter}
            onChange={setSuperviseeFilter}
          />
          <DropdownFilter
            label="Type"
            items={typeOptions}
            defaultValue={typeFilter}
            onChange={(values) =>
              setTypeFilter(values as Array<'single_client' | 'entire_caseload'>)
            }
          />
          <DropdownFilter
            label="Pod"
            items={data?.filters.teams || []}
            defaultValue={teamFilter}
            onChange={setTeamFilter}
          />
          <DropdownFilter
            label="Status"
            items={statusOptions}
            defaultValue={statusFilter}
            onChange={setStatusFilter}
          />
        </FilterBar>
      )}

      {/* Data Table */}
      <DataTable.Root>
        <DataTable.Header>
          <DataTable.Row>
            {canReview && (
              <DataTable.HeaderCell
                sortable
                sortDirection={sortColumn === 'clinician' ? sortDirection : null}
                onSort={() => handleSort('clinician')}
              >
                Supervisee
              </DataTable.HeaderCell>
            )}
            <DataTable.HeaderCell
              sortable
              sortDirection={sortColumn === 'type' ? sortDirection : null}
              onSort={() => handleSort('type')}
            >
              TYPE
            </DataTable.HeaderCell>
            {canReview && (
              <DataTable.HeaderCell
                sortable
                sortDirection={sortColumn === 'pod' ? sortDirection : null}
                onSort={() => handleSort('pod')}
              >
                POD
              </DataTable.HeaderCell>
            )}
            <DataTable.HeaderCell
              sortable
              sortDirection={sortColumn === 'effective_date' ? sortDirection : null}
              onSort={() => handleSort('effective_date')}
            >
              EFFECTIVE DATE
            </DataTable.HeaderCell>
            <DataTable.HeaderCell
              sortable
              sortDirection={sortColumn === 'created_at' ? sortDirection : null}
              onSort={() => handleSort('created_at')}
            >
              DATE REQUESTED
            </DataTable.HeaderCell>
            <DataTable.HeaderCell
              sortable
              sortDirection={sortColumn === 'status' ? sortDirection : null}
              onSort={() => handleSort('status')}
            >
              STATUS
            </DataTable.HeaderCell>
          </DataTable.Row>
        </DataTable.Header>

        <DataTable.Body>
          {items.length === 0 ? (
            <DataTable.Row>
              <DataTable.Cell colSpan={6} className="text-center text-charcoal-50">
                No reassignments found
              </DataTable.Cell>
            </DataTable.Row>
          ) : (
            items.map((reassignment) => (
              <DataTable.Row
                key={reassignment.id}
                onClick={() => handleRowClick(reassignment.id)}
                className="cursor-pointer hover:bg-sand-25"
              >
                {canReview && (
                  <DataTable.Cell label="Supervise">{reassignment.clinician.name}</DataTable.Cell>
                )}
                <DataTable.Cell label="Type">{formatType(reassignment)}</DataTable.Cell>
                {canReview && (
                  <DataTable.Cell label="Pod">{reassignment.clinician.pod || 'N/A'}</DataTable.Cell>
                )}
                <DataTable.Cell label="Effective Date">
                  {reassignment.effective_date
                    ? formatReassignmentDateLong(reassignment.effective_date)
                    : '—'}
                </DataTable.Cell>
                <DataTable.Cell label="Date Requested">
                  {formatDate(reassignment.created_at)}
                </DataTable.Cell>
                <DataTable.Cell label="Status">
                  <ReassignmentStatusBadge
                    status={reassignment.status}
                    familyInitiated={reassignment.family_initiated}
                    canReview={canReview}
                  />
                </DataTable.Cell>
              </DataTable.Row>
            ))
          )}
        </DataTable.Body>
      </DataTable.Root>

      <ReassignmentDetailWrapper
        reassignmentId={selectedReassignmentId}
        isOpen={isModalOpen}
        onClose={handleModalClose}
        canReview={canReview}
      />

      {/* Pagination */}
      {data?.pagination && (
        <div className="mt-4">
          <Pagination
            currentPage={data.pagination.page}
            totalPages={data.pagination.total_pages}
            totalCount={data.pagination.total_count}
            from={data.pagination.from}
            to={data.pagination.to}
            hasNextPage={data.pagination.has_next_page}
            hasPrevPage={data.pagination.has_prev_page}
            itemsPerPage={data.pagination.per_page}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
          />
        </div>
      )}
    </>
  )
}
